import React, { useState, useRef, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api from "../lib/api";
import { useAppConfig } from "../lib/config";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Upload, X, ImageIcon, Building2, Target, LineChart, Search, Mail, Sparkles, ArrowRight, ArrowLeft, EyeOff, RotateCcw, Snowflake, FileText } from "lucide-react";
import ReportModulesEditor, { normalizeReportModules } from "./client/ReportModulesEditor";
import { classifyScope } from "../lib/scopeClassifier";

const inputCls = "w-full bg-white border border-[#e2e8f0] rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all";
const labelCls = "text-[11px] uppercase tracking-widest text-[#718096] font-semibold";

const ACCEPTED = "image/png,image/jpeg,image/jpg,image/gif,image/webp";
const MAX_BYTES = 512 * 1024;

/**
 * Section wrapper — gives each block of the Edit Client form its own visual
 * grouping (subtle card + eyebrow heading + description). Makes a long form
 * scannable so the user can spot the block they want to edit without reading
 * every field label from top to bottom.
 */
function Section({ icon: Icon, title, description, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="rounded-2xl border border-[#edf2f7] bg-white p-5 sm:p-6 shadow-[0_1px_0_rgba(45,55,72,0.02)]"
    >
      <header className="flex items-start gap-3 mb-5 pb-4 border-b border-[#f7fafc]">
        <span className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" strokeWidth={2.2} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-base text-[#1a202c] leading-tight">{title}</h3>
          {description && (
            <p className="text-[11px] text-[#718096] mt-1 max-w-xl leading-[1.55]">{description}</p>
          )}
        </div>
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function ClientFormDialog({ open, onOpenChange, initial, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#f7fafc] border border-[#e2e8f0] rounded-2xl text-[#1a202c] max-w-4xl max-h-[92vh] shadow-2xl flex flex-col p-6 sm:p-7" data-testid="client-form-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display text-2xl text-[#1a202c]">
            {initial?.id ? "Edit client" : "New client"}
          </DialogTitle>
          <p className="text-xs text-[#718096] mt-1">
            {initial?.id ? "Update client details, integrations and reporting preferences." : "Set up the client, connect data sources and start tracking."}
          </p>
        </DialogHeader>
        {/* Key remounts the form body when switching between clients, resetting state cleanly. */}
        {open && (
          <ClientFormBody
            key={initial?.id || "new"}
            initial={initial}
            onSaved={onSaved}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ClientFormBody({ initial, onSaved, onCancel }) {
  const { max_keywords_per_client: KW_LIMIT } = useAppConfig();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);  // for the Assigned-SEO dropdown — /users/summary is readable by everyone
  const [form, setForm] = useState({
    name: initial?.name || "",
    domain: initial?.domain || "",
    location_name: initial?.location_name || "United Kingdom",
    language_name: initial?.language_name || "English",
    campaign_type: initial?.campaign_type || "national",
    service_location: initial?.service_location || "",
    gbp_cid: initial?.gbp_cid || "",
    gbp_business_name: initial?.gbp_business_name || "",
    search_radius_km: initial?.search_radius_km ?? 5,
    ga4_property_id: initial?.ga4_property_id || "",
    gsc_property_url: initial?.gsc_property_url || "",
    // Split-scope keyword lists. When editing an existing client we prefer
    // the new fields; falling back on the flat `keywords` list keeps things
    // working for the brief window before the server-side migration runs.
    national_keywords_text: (
      initial?.national_keywords && initial.national_keywords.length
        ? initial.national_keywords
        : (initial?.campaign_type === "local" ? [] : (initial?.keywords || []))
    ).join("\n"),
    local_keywords_text: (
      initial?.local_keywords && initial.local_keywords.length
        ? initial.local_keywords
        : (initial?.campaign_type === "local" ? (initial?.keywords || []) : [])
    ).join("\n"),
    notes: initial?.notes || "",
    report_email: initial?.report_email || "",
    client_first_name: initial?.client_first_name || "",
    logo_url: initial?.logo_url || "",
    assigned_user_id: initial?.assigned_user_id || "",
    account_manager_user_id: initial?.account_manager_user_id || "",
    // AI Visibility (GEO) — one prompt per line, platform checkboxes below.
    ai_prompts_text: (initial?.ai_prompts || []).join("\n"),
    ai_platforms: initial?.ai_platforms || [],
    // Brand Mentions Feed — DFS LLM Mentions API (Google AI + ChatGPT).
    brand_mentions_enabled: !!initial?.brand_mentions_enabled,
    frozen: !!initial?.frozen,
    report_modules: initial?.report_modules || null,
    // Per-client dismissed Suggested-competitor domains. Managed inline from
    // the Competitors tab's suggestion chips, surfaced here for review/undo.
    dismissed_competitor_suggestions: initial?.dismissed_competitor_suggestions || [],
  });
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [cidTesting, setCidTesting] = useState(false);
  const [cidResult, setCidResult] = useState(null); // { found, title, ... } | { found:false } | null
  const [ga4Testing, setGa4Testing] = useState(false);
  const [ga4Result, setGa4Result] = useState(null); // { ok, sessions_yesterday } | { ok:false, error } | null
  const [gscTesting, setGscTesting] = useState(false);
  const [gscResult, setGscResult] = useState(null);  // { ok, site_url } | { ok:false, error } | null
  const [gscSites, setGscSites] = useState(null);    // null = not loaded, [] = none, [...] = available
  const [gscSitesLoading, setGscSitesLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Load team members for the Assigned SEO dropdown. Public summary endpoint
  // — works for both admins and members (members just can't change the value).
  useEffect(() => {
    let alive = true;
    api.get("/users/summary")
      .then(({ data }) => { if (alive) setUsers(data.users || []); })
      .catch(() => { if (alive) setUsers([]); });
    return () => { alive = false; };
  }, []);

  // Live count (dedupe-across-both-lists) so the user sees the same total
  // the backend will enforce. Drives the X/limit counter and the disabled
  // state on the submit button. Also surfaces which keywords appear in both
  // lists (intentional double-tracking) so the user knows the cost impact.
  const keywordStats = useMemo(() => {
    const parse = (txt) => (txt || "")
      .split("\n")
      .map((s) => s.split(/\s+/).filter(Boolean).join(" "))
      .filter(Boolean);
    const natRaw = parse(form.national_keywords_text);
    const locRaw = parse(form.local_keywords_text);
    const nat = new Set(natRaw.map((k) => k.toLowerCase()));
    const loc = new Set(locRaw.map((k) => k.toLowerCase()));
    // Dedupe within each list first, then across.
    const both = new Set([...nat, ...loc]);
    const duplicates = [...nat].filter((k) => loc.has(k));
    return {
      nationalCount: nat.size,
      localCount: loc.size,
      unique: both.size,
      over: both.size > KW_LIMIT,
      duplicates,
    };
  }, [form.national_keywords_text, form.local_keywords_text, KW_LIMIT]);

  // Scope-recommendation hint. Runs the balanced heuristic classifier over
  // whatever's currently in each textarea and surfaces the mismatches so the
  // user can one-click move them to the "correct" list. Purely advisory —
  // false positives are safe because migration is user-confirmed.
  const scopeSuggestions = useMemo(() => {
    const parseList = (txt) => (txt || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const nat = parseList(form.national_keywords_text);
    const loc = parseList(form.local_keywords_text);
    const localInNational = nat.filter((k) => classifyScope(k) === "local");
    const nationalInLocal = loc.filter((k) => classifyScope(k) === "national");
    return { localInNational, nationalInLocal };
  }, [form.national_keywords_text, form.local_keywords_text]);

  // Track which keyword sets the user has explicitly dismissed so we don't
  // pester them after they've said "no, keep it here". Key is a sorted,
  // lower-cased signature of the flagged set — when the set changes (new
  // keywords typed) the signature changes and the hint re-appears, which
  // is the behaviour we want.
  // Detect the "Bromsgrove typed in the Country field" mistake. We reuse the
  // scope classifier's place-seed list: if the country entry classifies as
  // "local", it's a city/town — surface a warning so the user can move the
  // value to Service location instead of accidentally running "national"
  // keyword crawls at a hyperlocal SERP.
  const countryLooksLikeCity = useMemo(() => {
    const val = (form.location_name || "").trim();
    if (!val) return false;
    return classifyScope(val) === "local";
  }, [form.location_name]);

  const isLocalCampaign = form.campaign_type === "local";

  const [dismissedHints, setDismissedHints] = useState({ toLocal: "", toNational: "" });

  const sigFor = (list) => list.map((k) => k.toLowerCase()).sort().join("|");
  const sigLocalHint = sigFor(scopeSuggestions.localInNational);
  const sigNationalHint = sigFor(scopeSuggestions.nationalInLocal);
  const showLocalHint = sigLocalHint && sigLocalHint !== dismissedHints.toLocal;
  const showNationalHint = sigNationalHint && sigNationalHint !== dismissedHints.toNational;

  // Move suggested keywords from one list to the other (dedupe on arrival so
  // we don't create a duplicate the user has already got in the target list).
  const moveKeywords = (fromField, toField, keywordsToMove) => {
    const parse = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean);
    const fromList = parse(form[fromField]);
    const toList = parse(form[toField]);
    const moveSet = new Set(keywordsToMove.map((k) => k.toLowerCase()));
    const remaining = fromList.filter((k) => !moveSet.has(k.toLowerCase()));
    const existingTarget = new Set(toList.map((k) => k.toLowerCase()));
    const additions = keywordsToMove.filter((k) => !existingTarget.has(k.toLowerCase()));
    setForm((p) => ({
      ...p,
      [fromField]: remaining.join("\n"),
      [toField]: [...toList, ...additions].join("\n"),
    }));
    // Reset dismissal signatures so any *new* mismatches get re-flagged.
    setDismissedHints({ toLocal: "", toNational: "" });
  };

  const testCid = async () => {
    const raw = (form.gbp_cid || "").trim().replace(/^cid:/i, "");
    if (!raw) {
      toast.error("Paste a CID first");
      return;
    }
    setCidTesting(true);
    setCidResult(null);
    try {
      const { data } = await api.post("/seo/gbp/lookup", {
        cid: raw,
        location_name: form.service_location || form.location_name,
        language_name: form.language_name,
      });
      setCidResult(data);
      if (data.found) {
        toast.success(`Found: ${data.title}`);
        // Auto-fill the business name field if it's empty
        if (!form.gbp_business_name) {
          setForm((p) => ({ ...p, gbp_business_name: data.title }));
        }
      } else {
        toast.error("CID not found on DataForSEO Business Listings");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
      setCidResult({ found: false, error: true });
    } finally {
      setCidTesting(false);
    }
  };

  const testGa4 = async () => {
    const pid = (form.ga4_property_id || "").trim();
    if (!pid) {
      toast.error("Paste a GA4 property ID first");
      return;
    }
    setGa4Testing(true);
    setGa4Result(null);
    try {
      const { data } = await api.post("/seo/ga4/test", { property_id: pid });
      setGa4Result(data);
      toast.success(`Connected. ${data.sessions_yesterday ?? 0} sessions yesterday.`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Connection failed";
      setGa4Result({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setGa4Testing(false);
    }
  };

  const loadGscSites = async () => {
    setGscSitesLoading(true);
    try {
      const { data } = await api.get("/seo/gsc/sites");
      setGscSites(data?.sites || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Search Console sites");
      setGscSites([]);
    } finally {
      setGscSitesLoading(false);
    }
  };

  const testGsc = async () => {
    const site = (form.gsc_property_url || "").trim();
    if (!site) {
      toast.error("Pick or paste a Search Console property first");
      return;
    }
    setGscTesting(true);
    setGscResult(null);
    try {
      const { data } = await api.post("/seo/gsc/test", { site_url: site });
      setGscResult(data);
      toast.success("Search Console property verified.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Connection failed";
      setGscResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setGscTesting(false);
    }
  };

  const readAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const handleLogoFile = async (file) => {
    if (!file) return;
    if (!ACCEPTED.split(",").includes(file.type)) {
      toast.error("Logo must be PNG, JPG, GIF, or WebP.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Logo too large (${Math.round(file.size / 1024)} KB). Max 512 KB.`);
      return;
    }
    setLogoUploading(true);
    try {
      if (initial?.id) {
        // Existing client → upload immediately to /clients/{id}/logo
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/clients/${initial.id}/logo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((p) => ({ ...p, logo_url: data.logo_url || "" }));
        toast.success("Logo uploaded.");
      } else {
        // New client → read locally and stage in form; will be sent on Create
        const dataUrl = await readAsDataUrl(file);
        setForm((p) => ({ ...p, logo_url: dataUrl }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const clearLogo = async () => {
    if (initial?.id && form.logo_url) {
      try {
        await api.delete(`/clients/${initial.id}/logo`);
      } catch (e) {
        // Non-fatal — UI still clears the preview
      }
    }
    setForm((p) => ({ ...p, logo_url: "" }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Dedupe each scoped list independently (case-insensitive, whitespace
      // normalised) — duplicates ACROSS lists are intentional (they track
      // the same keyword at national + local SERPs) so we don't strip them.
      const dedupeList = (text) => {
        const raw = (text || "")
          .split("\n")
          .map((s) => s.split(/\s+/).filter(Boolean).join(" "))
          .filter(Boolean);
        const out = [];
        const seen = new Set();
        let dropped = 0;
        for (const kw of raw) {
          const key = kw.toLowerCase();
          if (seen.has(key)) { dropped += 1; continue; }
          seen.add(key);
          out.push(kw);
        }
        return { list: out, dropped };
      };
      const nat = dedupeList(form.national_keywords_text);
      const loc = dedupeList(form.local_keywords_text);
      const duplicatesRemoved = nat.dropped + loc.dropped;

      // Enforce the 100-total cap across the union (matches server-side).
      const unionSize = new Set([
        ...nat.list.map((k) => k.toLowerCase()),
        ...loc.list.map((k) => k.toLowerCase()),
      ]).size;
      if (unionSize > KW_LIMIT) {
        toast.error(
          `Keyword limit exceeded — this client has ${unionSize} unique keywords across the national and local lists but the server is configured to track a maximum of ${KW_LIMIT}.`,
        );
        setSaving(false);
        return;
      }

      const payload = {
        name: form.name.trim(),
        domain: form.domain.trim().replace(/^https?:\/\//, "").replace(/\/$/, ""),
        location_name: form.location_name,
        language_name: form.language_name,
        campaign_type: form.campaign_type,
        service_location: form.campaign_type === "local" ? (form.service_location?.trim() || null) : null,
        gbp_cid: form.campaign_type === "local" ? (form.gbp_cid?.trim().replace(/^cid:/i, "") || null) : null,
        gbp_business_name: form.campaign_type === "local" ? (form.gbp_business_name?.trim() || null) : null,
        ga4_property_id: form.ga4_property_id?.trim() || null,
        gsc_property_url: form.gsc_property_url?.trim() || null,
        search_radius_km: form.campaign_type === "local" ? Math.max(1, Math.min(50, parseInt(form.search_radius_km, 10) || 5)) : 5,
        national_keywords: nat.list,
        local_keywords: loc.list,
        notes: form.notes,
        report_email: form.report_email?.trim() || null,
        client_first_name: form.client_first_name?.trim() || null,
        logo_url: form.logo_url?.trim() || null,
        assigned_user_id: form.assigned_user_id || null,
        account_manager_user_id: form.account_manager_user_id || null,
        // AI Visibility — 20-max, one prompt per line, deduped.
        ai_prompts: Array.from(new Set(
          (form.ai_prompts_text || "").split("\n").map((s) => s.trim()).filter(Boolean).map((s) => s.slice(0, 500))
        )).slice(0, 20),
        ai_platforms: form.ai_platforms || [],
        brand_mentions_enabled: !!form.brand_mentions_enabled,
        frozen: !!form.frozen,
        report_modules: normalizeReportModules(form.report_modules).map(({ key, enabled }) => ({ key, enabled })),
        dismissed_competitor_suggestions: form.dismissed_competitor_suggestions || [],
      };
      let res;
      if (initial?.id) {
        res = await api.patch(`/clients/${initial.id}`, payload);
        if (duplicatesRemoved > 0) {
          toast.success(
            `Client updated. Removed ${duplicatesRemoved} duplicate keyword${duplicatesRemoved === 1 ? "" : "s"}.`,
          );
        } else {
          toast.success("Client updated.");
        }
      } else {
        res = await api.post("/clients", payload);
        if (duplicatesRemoved > 0) {
          toast.success(
            `Client created. Removed ${duplicatesRemoved} duplicate keyword${duplicatesRemoved === 1 ? "" : "s"}.`,
          );
        }
      }
      // Reflect the deduped lists back into the textareas so the user sees the cleaned-up state
      setForm((f) => ({
        ...f,
        national_keywords_text: nat.list.join("\n"),
        local_keywords_text: loc.list.join("\n"),
      }));
      onSaved?.(res.data);
    } catch (err) {
      // FastAPI 422 returns `detail` as an array of {loc, msg, type} objects —
      // never a plain string. Coerce defensively so a future validation error
      // can't crash the React tree by passing a non-string to toast.error.
      const detail = err?.response?.data?.detail;
      let message = "Save failed";
      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail
          .map((d) => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d))))
          .join(" · ");
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 mt-3 min-h-0 flex-1">
      <div className="space-y-4 overflow-y-auto pr-2 -mr-2 flex-1 min-h-0">
      {/* ────────────────────── 1. CLIENT IDENTITY ────────────────────── */}
      <Section
        icon={Building2}
        title="Client identity"
        description="Basics that appear on the dashboard, reports and PDF cover — logo, name, domain and target market."
        testid="client-form-section-identity"
      >
        <div>
          <label className={labelCls}>Client logo</label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleLogoFile(f);
            }}
            className={`mt-1.5 flex items-center gap-4 rounded-xl border-2 border-dashed p-3 transition-colors ${
              dragOver ? "border-[#4fd1c5] bg-[#4fd1c5]/5" : "border-[#e2e8f0] bg-[#f7fafc]"
            }`}
            data-testid="client-form-logo-dropzone"
          >
            <div className="h-16 w-16 rounded-xl bg-white border border-[#e2e8f0] flex items-center justify-center overflow-hidden shrink-0">
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo preview"
                  className="h-full w-full object-contain p-1"
                  data-testid="client-form-logo-preview"
                />
              ) : (
                <ImageIcon className="h-5 w-5 text-[#a0aec0]" strokeWidth={1.8} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#1a202c] font-semibold">
                {form.logo_url ? "Logo selected" : "Drag & drop or click to upload"}
              </div>
              <div className="text-[11px] text-[#a0aec0] mt-0.5">
                PNG, JPG, GIF, WebP · up to 512 KB
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={logoUploading}
                  data-testid="client-form-logo-pick"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2c7a7b] hover:text-[#234e52] disabled:opacity-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {logoUploading ? "Uploading…" : (form.logo_url ? "Replace" : "Choose file")}
                </button>
                {form.logo_url && (
                  <button
                    type="button"
                    onClick={clearLogo}
                    data-testid="client-form-logo-clear"
                    className="inline-flex items-center gap-1.5 text-xs text-[#f56565] hover:text-[#c53030]"
                  >
                    <X className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              hidden
              data-testid="client-form-logo-input"
              onChange={(e) => handleLogoFile(e.target.files?.[0])}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Client name</label>
            <input
              required
              data-testid="client-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={`${inputCls} mt-1.5`}
              placeholder="Acme Corp"
            />
          </div>
          <div>
            <label className={labelCls}>Primary domain</label>
            <input
              required
              data-testid="client-form-domain"
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              className={`${inputCls} mt-1.5 font-mono-tight`}
              placeholder="acme.com"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Country</label>
            <input
              value={form.location_name}
              onChange={(e) => setForm({ ...form, location_name: e.target.value })}
              className={`${inputCls} mt-1.5`}
              placeholder="United Kingdom"
              data-testid="client-form-location"
            />
            <div className="text-[10px] text-[#a0aec0] mt-1">
              Country-level DataForSEO location. National keywords are tracked against this SERP. For city / service-area targeting, use Campaign type = Local + Service location below.
            </div>
            {countryLooksLikeCity && (
              <div
                data-testid="client-form-country-warning"
                className="mt-2 rounded-xl border border-[#f6ad55]/50 bg-[#fefcbf]/60 px-3 py-2.5 text-[11px] text-[#744210]"
              >
                <div className="flex items-start gap-2">
                  <Sparkles className="h-3.5 w-3.5 mt-0.5 text-[#b7791f] shrink-0" strokeWidth={2.2} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-tight">
                      &quot;{form.location_name}&quot; looks like a city, not a country
                    </div>
                    <div className="mt-0.5 text-[#975a16]">
                      National keywords should target a country-level SERP (e.g. <span className="font-mono-tight">United Kingdom</span>). Move city/town targeting to <strong>Service location</strong> under Campaign settings.
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setForm((p) => ({
                          ...p,
                          location_name: "United Kingdom",
                          campaign_type: "local",
                          service_location: p.service_location || p.location_name,
                        }))}
                        data-testid="client-form-country-fix"
                        className="inline-flex items-center gap-1 rounded-lg bg-[#b7791f] hover:bg-[#975a16] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors"
                      >
                        Fix — set UK + Local
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className={labelCls}>Language</label>
            <input
              value={form.language_name}
              onChange={(e) => setForm({ ...form, language_name: e.target.value })}
              className={`${inputCls} mt-1.5`}
              placeholder="English"
              data-testid="client-form-language"
            />
          </div>
        </div>
      </Section>

      {/* ────────────────────── 2. CAMPAIGN SETTINGS ────────────────────── */}
      <Section
        icon={Target}
        title="Campaign settings"
        description="Choose National to track country-level SERPs, or Local for city / service-area SERPs plus Google Local Pack tracking."
        testid="client-form-section-campaign"
      >
        <div>
          <label className={labelCls}>Campaign type</label>
          <div className="mt-1.5 flex gap-2" data-testid="client-form-campaign-type">
            {[
              { v: "national", label: "National", hint: "Country-level SERP" },
              { v: "local", label: "Local", hint: "City / service-area SERP + Local Pack" },
            ].map((opt) => {
              const active = form.campaign_type === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm({ ...form, campaign_type: opt.v })}
                  data-testid={`client-form-campaign-${opt.v}`}
                  className={`flex-1 rounded-xl px-3.5 py-2.5 text-sm font-semibold border transition-all text-left ${
                    active
                      ? "bg-[#4fd1c5]/10 border-[#4fd1c5] text-[#234e52] ring-2 ring-[#4fd1c5]/25"
                      : "bg-white border-[#e2e8f0] text-[#4a5568] hover:border-[#cbd5e0]"
                  }`}
                >
                  <div className="text-sm">{opt.label}</div>
                  <div className="text-[10px] font-normal text-[#a0aec0] mt-0.5">{opt.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
        {form.campaign_type === "local" && (
          <div>
            <label className={labelCls}>Service location</label>
            <input
              value={form.service_location}
              onChange={(e) => setForm({ ...form, service_location: e.target.value })}
              className={`${inputCls} mt-1.5 font-mono-tight`}
              placeholder="e.g. Bromsgrove,England,United Kingdom"
              data-testid="client-form-service-location"
            />
            <div className="text-[10px] text-[#a0aec0] mt-1">
              DataForSEO geo-target (city, town, or postcode). Used for SERP location targeting + Local Pack tracking.
            </div>
          </div>
        )}
        {form.campaign_type === "local" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>GBP business name</label>
              <input
                value={form.gbp_business_name}
                onChange={(e) => setForm({ ...form, gbp_business_name: e.target.value })}
                className={`${inputCls} mt-1.5`}
                placeholder="exact name on Google Maps"
                data-testid="client-form-gbp-name"
              />
              <div className="text-[10px] text-[#a0aec0] mt-1">
                Optional — used for Local Pack matching when blank fallback is the client name.
              </div>
            </div>
            <div>
              <label className={labelCls}>GBP CID</label>
              <div className="mt-1.5 flex gap-2">
                <input
                  value={form.gbp_cid}
                  onChange={(e) => { setForm({ ...form, gbp_cid: e.target.value }); setCidResult(null); }}
                  className={`${inputCls} font-mono-tight flex-1`}
                  placeholder="e.g. 5003644783714159453"
                  data-testid="client-form-gbp-cid"
                />
                <button
                  type="button"
                  onClick={testCid}
                  disabled={cidTesting || !(form.gbp_cid || "").trim()}
                  data-testid="client-form-gbp-cid-test"
                  className="shrink-0 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest border border-[#e2e8f0] hover:border-[#4fd1c5] hover:text-[#2c7a7b] text-[#4a5568] bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {cidTesting ? "Testing…" : "Test"}
                </button>
              </div>
              <div className="text-[10px] text-[#a0aec0] mt-1">
                Optional — paste from your Google Maps URL (`?cid=…`) for exact GBP matching.
              </div>
              {cidResult && (
                <div
                  data-testid="client-form-gbp-cid-result"
                  className={`mt-2 text-xs rounded-xl p-3 border ${
                    cidResult.found
                      ? "bg-[#4fd1c5]/10 border-[#4fd1c5] text-[#234e52]"
                      : "bg-[#fed7d7]/30 border-[#feb2b2] text-[#742a2a]"
                  }`}
                >
                  {cidResult.found ? (
                    <div>
                      <div className="font-semibold flex items-center gap-1.5">
                        {cidResult.logo && <img src={cidResult.logo} alt="" className="h-5 w-5 rounded-full" />}
                        {cidResult.title}
                      </div>
                      <div className="text-[10px] text-[#234e52]/70 font-mono-tight mt-1 space-y-0.5">
                        <div>{cidResult.category} · {cidResult.address}</div>
                        {cidResult.phone && <div>{cidResult.phone}</div>}
                        {cidResult.rating_value != null && (
                          <div>⭐ {cidResult.rating_value} · {cidResult.rating_votes} reviews · {cidResult.total_photos ?? 0} photos</div>
                        )}
                        {cidResult.domain && <div>{cidResult.domain}</div>}
                      </div>
                    </div>
                  ) : (
                    <div className="font-mono-tight">No business found for this CID on DataForSEO. Double-check the number from your Google Maps URL.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {form.campaign_type === "local" && (
          <div>
            <label className={labelCls}>Hyper-local search radius</label>
            <div className="mt-1.5 flex items-center gap-3">
              <input
                type="range"
                min="1"
                max="25"
                step="1"
                value={form.search_radius_km}
                onChange={(e) => setForm({ ...form, search_radius_km: parseInt(e.target.value, 10) })}
                data-testid="client-form-search-radius"
                className="flex-1 accent-[#4fd1c5]"
              />
              <div className="font-mono-tight text-sm text-[#234e52] w-14 text-right">{form.search_radius_km} km</div>
            </div>
            <div className="text-[10px] text-[#a0aec0] mt-1">
              When this client&apos;s GBP coordinates have been captured (auto-fetched via the Test button above), Local Finder searches from those coordinates instead of the town centroid. Catches businesses on the edges of a town that the centroid-based search misses.
            </div>
          </div>
        )}
      </Section>

      {/* ────────────────────── 3. ANALYTICS INTEGRATIONS ────────────────────── */}
      <Section
        icon={LineChart}
        title="Analytics integrations"
        description="Connect Google Analytics 4 and Search Console to enrich the monthly digest with sessions, conversions and top queries."
        testid="client-form-section-analytics"
      >
        <div>
          <label className={labelCls}>GA4 property ID</label>
          <div className="mt-1.5 flex gap-2">
            <input
              value={form.ga4_property_id}
              onChange={(e) => { setForm({ ...form, ga4_property_id: e.target.value }); setGa4Result(null); }}
              className={`${inputCls} font-mono-tight flex-1`}
              placeholder="e.g. 123456789 (numeric — not G-XXXX)"
              data-testid="client-form-ga4-property"
            />
            <button
              type="button"
              onClick={testGa4}
              disabled={ga4Testing || !(form.ga4_property_id || "").trim()}
              data-testid="client-form-ga4-test"
              className="shrink-0 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest border border-[#e2e8f0] hover:border-[#4fd1c5] hover:text-[#2c7a7b] text-[#4a5568] bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {ga4Testing ? "Testing…" : "Test connection"}
            </button>
          </div>
          <div className="text-[10px] text-[#a0aec0] mt-1">
            Optional — found in GA4 → Admin → Property settings. Grant <span className="font-mono-tight">seo-service-account@seo-dashboard-498818.iam.gserviceaccount.com</span> Viewer access in the property&apos;s access management.
          </div>
          {ga4Result && (
            <div
              data-testid="client-form-ga4-result"
              className={`mt-2 text-xs rounded-xl p-3 border ${
                ga4Result.ok
                  ? "bg-[#4fd1c5]/10 border-[#4fd1c5] text-[#234e52]"
                  : "bg-[#fed7d7]/30 border-[#feb2b2] text-[#742a2a]"
              }`}
            >
              {ga4Result.ok ? (
                <div>
                  <div className="font-semibold">Connected to property {ga4Result.property_id}</div>
                  <div className="text-[10px] text-[#234e52]/70 font-mono-tight mt-1">
                    Yesterday&apos;s sessions: {Math.round(ga4Result.sessions_yesterday ?? 0).toLocaleString()}
                  </div>
                </div>
              ) : (
                <div className="font-mono-tight">{ga4Result.error || "Connection failed."}</div>
              )}
            </div>
          )}
        </div>
        <div>
          <label className={labelCls}>Search Console property</label>
          <div className="mt-1.5 flex gap-2">
            <input
              value={form.gsc_property_url}
              onChange={(e) => { setForm({ ...form, gsc_property_url: e.target.value }); setGscResult(null); }}
              className={`${inputCls} font-mono-tight flex-1`}
              placeholder="sc-domain:example.com  or  https://example.com/"
              data-testid="client-form-gsc-property"
            />
            <button
              type="button"
              onClick={loadGscSites}
              disabled={gscSitesLoading}
              data-testid="client-form-gsc-load-sites"
              className="shrink-0 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest border border-[#e2e8f0] hover:border-[#4fd1c5] hover:text-[#2c7a7b] text-[#4a5568] bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {gscSitesLoading ? "Loading…" : "Pick from list"}
            </button>
            <button
              type="button"
              onClick={testGsc}
              disabled={gscTesting || !(form.gsc_property_url || "").trim()}
              data-testid="client-form-gsc-test"
              className="shrink-0 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest border border-[#e2e8f0] hover:border-[#4fd1c5] hover:text-[#2c7a7b] text-[#4a5568] bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {gscTesting ? "Testing…" : "Test"}
            </button>
          </div>
          {gscSites !== null && gscSites.length > 0 && (
            <div className="mt-2 rounded-xl border border-[#edf2f7] bg-[#f7fafc] p-2 max-h-44 overflow-y-auto" data-testid="client-form-gsc-site-list">
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold px-2 py-1">Your verified properties</div>
              {gscSites.map((s) => (
                <button
                  key={s.site_url}
                  type="button"
                  onClick={() => { setForm({ ...form, gsc_property_url: s.site_url }); setGscResult(null); }}
                  className="w-full text-left px-2 py-1.5 text-xs font-mono-tight rounded-lg hover:bg-white hover:text-[#2c7a7b] text-[#4a5568] transition-colors flex items-center justify-between gap-2"
                >
                  <span className="truncate">{s.site_url}</span>
                  <span className="shrink-0 text-[9px] uppercase tracking-widest text-[#a0aec0]">{s.permission_level}</span>
                </button>
              ))}
            </div>
          )}
          {gscSites !== null && gscSites.length === 0 && (
            <div className="mt-2 text-[10px] text-[#a0aec0] font-mono-tight">
              No verified Search Console properties found for the connected Google account.
            </div>
          )}
          <div className="text-[10px] text-[#a0aec0] mt-1">
            Optional — pulls top queries, top pages, and striking-distance opportunities from Search Console. Uses the same Google connection as GA4. <span className="font-mono-tight">sc-domain:</span> prefix = domain property, <span className="font-mono-tight">https://</span> = URL-prefix property.
          </div>
          {gscResult && (
            <div
              data-testid="client-form-gsc-result"
              className={`mt-2 text-xs rounded-xl p-3 border ${
                gscResult.ok
                  ? "bg-[#4fd1c5]/10 border-[#4fd1c5] text-[#234e52]"
                  : "bg-[#fed7d7]/30 border-[#feb2b2] text-[#742a2a]"
              }`}
            >
              {gscResult.ok ? (
                <div className="font-semibold">Verified: {gscResult.site_url}</div>
              ) : (
                <div className="font-mono-tight">{gscResult.error || "Connection failed."}</div>
              )}
            </div>
          )}
        </div>
      </Section>

      {/* ────────────────────── 4. KEYWORDS & NOTES ────────────────────── */}
      <Section
        icon={Search}
        title="Keywords & internal notes"
        description={
          isLocalCampaign
            ? "Split your tracked keywords into two lists — National terms track against the country SERP, Local terms against the service-area SERP. The 100-keyword limit applies to the two lists combined. Notes are agency-only and never surface in client-facing reports."
            : "Tracked keywords are polled weekly against the country SERP set in Client identity. Switch Campaign type to Local (in the Campaign section above) to also track city / service-area terms. Notes are agency-only and never surface in client-facing reports."
        }
        testid="client-form-section-keywords"
      >
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">
            {isLocalCampaign
              ? <>Tracked keywords · {keywordStats.nationalCount} national · {keywordStats.localCount} local</>
              : <>Tracked keywords · {keywordStats.nationalCount} total</>}
          </div>
          <span
            data-testid="client-form-keywords-count"
            className={`text-[10px] font-mono-tight tabular-nums ${
              keywordStats.over ? "text-[#c53030] font-semibold" : "text-[#a0aec0]"
            }`}
          >
            {keywordStats.unique} / {KW_LIMIT} unique
          </span>
        </div>
        <div className={`grid grid-cols-1 ${isLocalCampaign ? "lg:grid-cols-2" : ""} gap-4`}>
          <div>
            <label className={labelCls}>
              {isLocalCampaign ? "National keywords · country SERP" : "Tracked keywords · country SERP"}
            </label>
            <textarea
              data-testid="client-form-national-keywords"
              value={form.national_keywords_text}
              onChange={(e) => setForm({ ...form, national_keywords_text: e.target.value })}
              rows={5}
              className={`${inputCls} mt-1.5 resize-none font-mono-tight ${
                keywordStats.over ? "!border-[#f56565] focus:!border-[#f56565] focus:!ring-[#f56565]/15" : ""
              }`}
              placeholder={"seo dashboard\nrank tracking software\nbacklink analysis tool"}
              aria-invalid={keywordStats.over ? "true" : "false"}
            />
            <div className="mt-1.5 text-[10px] text-[#a0aec0]">
              Tracked against <span className="font-mono-tight">{form.location_name || "United Kingdom"}</span>. One per line.
            </div>
            {showLocalHint && (
              <div
                data-testid="client-form-scope-hint-to-local"
                className="mt-2 rounded-xl border border-[#4fd1c5]/40 bg-[#4fd1c5]/8 px-3 py-2.5 text-[11px] text-[#234e52]"
              >
                <div className="flex items-start gap-2">
                  <Sparkles className="h-3.5 w-3.5 mt-0.5 text-[#2c7a7b] shrink-0" strokeWidth={2.2} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-tight">
                      {scopeSuggestions.localInNational.length} look local
                    </div>
                    <div className="mt-0.5 text-[#2c7a7b]/85 font-mono-tight break-words">
                      {scopeSuggestions.localInNational.slice(0, 3).map((k) => `"${k}"`).join(", ")}
                      {scopeSuggestions.localInNational.length > 3 && ` +${scopeSuggestions.localInNational.length - 3} more`}
                    </div>
                    {!isLocalCampaign && (
                      <div className="mt-1 text-[10px] text-[#2c7a7b]/70">
                        Campaign is National — switch to Local to get proper geo-targeted SERPs for these.
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (isLocalCampaign) {
                            moveKeywords("national_keywords_text", "local_keywords_text", scopeSuggestions.localInNational);
                          } else {
                            // Switching to Local also moves the flagged keywords across in one action.
                            setForm((p) => ({ ...p, campaign_type: "local" }));
                            setTimeout(() => moveKeywords("national_keywords_text", "local_keywords_text", scopeSuggestions.localInNational), 0);
                          }
                        }}
                        data-testid="client-form-scope-hint-move-to-local"
                        className="inline-flex items-center gap-1 rounded-lg bg-[#2c7a7b] hover:bg-[#234e52] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors"
                      >
                        {isLocalCampaign ? "Move to local" : "Switch to Local"} <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDismissedHints((p) => ({ ...p, toLocal: sigLocalHint }))}
                        data-testid="client-form-scope-hint-dismiss-local"
                        className="text-[10px] text-[#4a5568] hover:text-[#1a202c] uppercase tracking-widest font-semibold"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {isLocalCampaign && (
          <div>
            <label className={labelCls}>Local keywords · service-area SERP</label>
            <textarea
              data-testid="client-form-local-keywords"
              value={form.local_keywords_text}
              onChange={(e) => setForm({ ...form, local_keywords_text: e.target.value })}
              rows={5}
              className={`${inputCls} mt-1.5 resize-none font-mono-tight ${
                keywordStats.over ? "!border-[#f56565] focus:!border-[#f56565] focus:!ring-[#f56565]/15" : ""
              }`}
              placeholder={"pet shop bromsgrove\nlocal grooming near me\ndog trainer worcester"}
              aria-invalid={keywordStats.over ? "true" : "false"}
            />
            <div className="mt-1.5 text-[10px] text-[#a0aec0]">
              {form.service_location
                ? <>Tracked against <span className="font-mono-tight">{form.service_location}</span>. One per line.</>
                : <>Tracked against <span className="font-mono-tight">{form.location_name || "United Kingdom"}</span> (no service location set). Set a Service location in the Campaign section for geo-targeted SERPs.</>}
            </div>
            {showNationalHint && (
              <div
                data-testid="client-form-scope-hint-to-national"
                className="mt-2 rounded-xl border border-[#f6ad55]/50 bg-[#fefcbf]/60 px-3 py-2.5 text-[11px] text-[#744210]"
              >
                <div className="flex items-start gap-2">
                  <Sparkles className="h-3.5 w-3.5 mt-0.5 text-[#b7791f] shrink-0" strokeWidth={2.2} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-tight">
                      {scopeSuggestions.nationalInLocal.length} look national
                    </div>
                    <div className="mt-0.5 text-[#975a16] font-mono-tight break-words">
                      {scopeSuggestions.nationalInLocal.slice(0, 3).map((k) => `"${k}"`).join(", ")}
                      {scopeSuggestions.nationalInLocal.length > 3 && ` +${scopeSuggestions.nationalInLocal.length - 3} more`}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => moveKeywords("local_keywords_text", "national_keywords_text", scopeSuggestions.nationalInLocal)}
                        data-testid="client-form-scope-hint-move-to-national"
                        className="inline-flex items-center gap-1 rounded-lg bg-[#b7791f] hover:bg-[#975a16] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors"
                      >
                        <ArrowLeft className="h-3 w-3" strokeWidth={2.5} /> Move to national
                      </button>
                      <button
                        type="button"
                        onClick={() => setDismissedHints((p) => ({ ...p, toNational: sigNationalHint }))}
                        data-testid="client-form-scope-hint-dismiss-national"
                        className="text-[10px] text-[#744210]/80 hover:text-[#744210] uppercase tracking-widest font-semibold"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
        {!isLocalCampaign && keywordStats.localCount > 0 && (
          <div
            data-testid="client-form-hidden-local-warning"
            className="rounded-xl border border-[#fbbf24]/40 bg-[#fef3c7] px-3 py-2.5 text-[11px] text-[#78350f]"
          >
            <div className="font-semibold">
              {keywordStats.localCount} local keyword{keywordStats.localCount === 1 ? "" : "s"} hidden
            </div>
            <div className="mt-0.5 text-[#78350f]/85">
              This client is set to <strong>National</strong> campaign, so the Local keywords list is hidden. They&apos;re still stored — switch to Local to see them, or merge them into the National list.
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, campaign_type: "local" }))}
                data-testid="client-form-hidden-local-switch"
                className="inline-flex items-center gap-1 rounded-lg bg-[#b7791f] hover:bg-[#975a16] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors"
              >
                Switch to Local
              </button>
              <button
                type="button"
                onClick={() => {
                  // Merge local_keywords_text into national_keywords_text (dedupe case-insensitively) and clear local.
                  const parse = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean);
                  const nat = parse(form.national_keywords_text);
                  const loc = parse(form.local_keywords_text);
                  const existing = new Set(nat.map((k) => k.toLowerCase()));
                  const additions = loc.filter((k) => !existing.has(k.toLowerCase()));
                  setForm((p) => ({
                    ...p,
                    national_keywords_text: [...nat, ...additions].join("\n"),
                    local_keywords_text: "",
                  }));
                }}
                data-testid="client-form-hidden-local-merge"
                className="text-[10px] text-[#78350f] hover:text-[#5a2c0a] uppercase tracking-widest font-semibold underline decoration-dotted"
              >
                Merge into National
              </button>
            </div>
          </div>
        )}
        {keywordStats.duplicates.length > 0 && (
          <div
            data-testid="client-form-keywords-duplicates-info"
            className="rounded-xl border border-[#fbbf24]/40 bg-[#fef3c7] px-3 py-2 text-[11px] text-[#78350f]"
          >
            <strong className="font-semibold">Tracked at both scopes:</strong>{" "}
            {keywordStats.duplicates.slice(0, 5).join(", ")}
            {keywordStats.duplicates.length > 5 && ` +${keywordStats.duplicates.length - 5} more`}. Each will get 2 SERP calls per week (once nationally, once locally) — useful when you want to see &quot;#7 nationwide, #2 in town&quot;.
          </div>
        )}
        {keywordStats.over ? (
          <div
            data-testid="client-form-keywords-limit-error"
            className="text-[11px] text-[#c53030] font-semibold"
          >
            {isLocalCampaign
              ? <>Too many keywords across the two lists — limit is {KW_LIMIT} unique per client. Remove {keywordStats.unique - KW_LIMIT} to save.</>
              : <>Too many keywords — limit is {KW_LIMIT} per client. Remove {keywordStats.unique - KW_LIMIT} to save.</>}
          </div>
        ) : (
          <div className="text-[10px] text-[#a0aec0]">
            {isLocalCampaign
              ? <>Limit: {KW_LIMIT} unique keywords max per client (across both lists). Duplicates within a single list are removed automatically.</>
              : <>Limit: {KW_LIMIT} keywords max per client. Duplicates are removed automatically.</>}
          </div>
        )}
        <div>
          <label className={labelCls}>Notes</label>
          <textarea
            data-testid="client-form-notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className={`${inputCls} mt-1.5 resize-none`}
            placeholder="Internal notes — never shown to the client."
          />
        </div>
      </Section>

      {/* ────────────────────── 5. AI VISIBILITY (GEO) ────────────────────── */}
      <Section
        icon={Sparkles}
        title="AI Visibility (GEO)"
        description="Track how ChatGPT, Claude, Gemini and Perplexity mention this client. Prompts run monthly on the 1st. Zero-cost default — nothing runs until you enable at least one platform."
        testid="client-form-section-ai-visibility"
      >
        <div>
          <label className={labelCls}>Enabled LLM platforms</label>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            {[
              { id: "chat_gpt", label: "ChatGPT", color: "#10a37f" },
              { id: "claude", label: "Claude", color: "#d97706" },
              { id: "gemini", label: "Gemini", color: "#4285f4" },
              { id: "perplexity", label: "Perplexity", color: "#20b8cd" },
            ].map((p) => {
              const on = (form.ai_platforms || []).includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setForm((f) => ({
                    ...f,
                    ai_platforms: on
                      ? f.ai_platforms.filter((x) => x !== p.id)
                      : [...(f.ai_platforms || []), p.id],
                  }))}
                  data-testid={`client-form-ai-platform-${p.id}`}
                  style={on ? { background: p.color, borderColor: p.color, color: "white" } : {}}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-bold uppercase tracking-widest transition-colors ${
                    on ? "" : "bg-white border-[#e2e8f0] text-[#4a5568]"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="text-[10px] text-[#a0aec0] mt-1.5">
            Each enabled platform runs one call per prompt per month. Roughly $0.01–0.02 per call depending on model.
          </div>
        </div>
        <div>
          <label className={labelCls}>Tracked prompts <span className="text-[#a0aec0] font-mono-tight text-[10px]">· one per line · max 20</span></label>
          <textarea
            data-testid="client-form-ai-prompts"
            value={form.ai_prompts_text}
            onChange={(e) => setForm({ ...form, ai_prompts_text: e.target.value })}
            rows={5}
            className={`${inputCls} mt-1.5 resize-none font-mono-tight`}
            placeholder={"best HR consultancy in Surrey\noutsourced HR pros and cons\nHR support for small businesses UK"}
          />
          <div className="text-[10px] text-[#a0aec0] mt-1">
            Natural questions your customers might ask an AI. Max 500 characters per prompt.
          </div>
        </div>

        {/* Brand Mentions Feed toggle — a separate DFS API (LLM Mentions vs LLM Responses) */}
        <div className="pt-3 border-t border-dashed border-[#e2e8f0]">
          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="client-form-brand-mentions-toggle-label">
            <input
              type="checkbox"
              checked={!!form.brand_mentions_enabled}
              onChange={(e) => setForm({ ...form, brand_mentions_enabled: e.target.checked })}
              data-testid="client-form-brand-mentions-toggle"
              className="mt-1 h-4 w-4 rounded border-[#cbd5e0] text-[#38b2ac] focus:ring-[#4fd1c5]"
            />
            <div className="flex-1">
              <div className="text-[13px] font-bold text-[#1a202c]">Enable Brand Mentions Feed</div>
              <div className="text-[11px] text-[#718096] mt-0.5">
                Surfaces the "Brand Mentions" tab on this client — a live feed of Google AI Overview + ChatGPT answers that cite <span className="font-mono-tight text-[#2c7a7b]">{form.domain || "this domain"}</span>. Backed by DataForSEO's LLM Mentions API.
              </div>
              <div className="text-[10px] text-[#a0aec0] mt-1">
                Runs monthly on the 1st alongside the AI Visibility crawl — <strong>~$0.25 per client per month</strong>. Manual refreshes cost ~$0.10 per platform and are cached for 6 hours. ChatGPT mentions are US/English only per DFS.
              </div>
            </div>
          </label>
        </div>
      </Section>

      {/* ────────────────────── 6. REPORTING & ASSIGNMENT ────────────────────── */}
      <Section
        icon={Mail}
        title="Reporting & assignment"
        description="Who receives the monthly digest email and which team member owns this client."
        testid="client-form-section-reporting"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Client contact first name</label>
            <input
              type="text"
              data-testid="client-form-client-first-name"
              value={form.client_first_name}
              onChange={(e) => setForm({ ...form, client_first_name: e.target.value })}
              placeholder="Sarah"
              className={`${inputCls} mt-1.5`}
            />
            <div className="mt-1 text-[10px] text-[#a0aec0]">
              Used to personalise the monthly digest email greeting (&quot;Hi Sarah,&quot;). Leave blank for a generic &quot;Hi there,&quot;.
            </div>
          </div>
          <div>
            <label className={labelCls}>Monthly digest email(s)</label>
            <input
              type="text"
              data-testid="client-form-report-email"
              value={form.report_email}
              onChange={(e) => setForm({ ...form, report_email: e.target.value })}
              placeholder="reports@client.com, accountant@client.com"
              className={`${inputCls} mt-1.5 font-mono-tight`}
            />
            <div className="mt-1 text-[10px] text-[#a0aec0]">
              Separate multiple addresses with commas. Sends to everyone in Bcc so recipients can&apos;t see each other.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Assigned SEO</label>
            <select
              data-testid="client-form-assigned-user"
              value={form.assigned_user_id || ""}
              onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })}
              className={`${inputCls} mt-1.5`}
            >
              <option value="">— Unassigned —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.email ? ` (${u.email})` : ""}{u.role === "admin" ? " · admin" : ""}
                </option>
              ))}
            </select>
            <div className="mt-1 text-[10px] text-[#a0aec0]">
              The team member who does the SEO work on this account.
            </div>
          </div>
          <div>
            <label className={labelCls}>Account manager</label>
            <select
              data-testid="client-form-account-manager"
              value={form.account_manager_user_id || ""}
              onChange={(e) => setForm({ ...form, account_manager_user_id: e.target.value })}
              className={`${inputCls} mt-1.5`}
            >
              <option value="">— Unassigned —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.email ? ` (${u.email})` : ""}{u.role === "admin" ? " · admin" : ""}
                </option>
              ))}
            </select>
            <div className="mt-1 text-[10px] text-[#a0aec0]">
              The client-facing lead who owns the relationship.
            </div>
          </div>
        </div>
      </Section>

      {/* Hidden competitor suggestions — only renders when there's something to show */}
      {(form.dismissed_competitor_suggestions || []).length > 0 && (
        <Section
          icon={EyeOff}
          title="Hidden competitor suggestions"
          description="Domains you've dismissed from the Suggested competitors rail on this client. Click to unhide — they'll reappear on the next refresh."
          testid="client-form-section-dismissed-competitors"
        >
          <div className="flex flex-wrap gap-2" data-testid="client-form-dismissed-competitor-chips">
            {(form.dismissed_competitor_suggestions || []).map((dom) => (
              <div
                key={dom}
                data-testid={`client-form-dismissed-chip-${dom}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#e2e8f0] bg-[#f7fafc] px-2.5 py-1 text-xs text-[#4a5568] font-mono-tight"
              >
                {dom}
                <button
                  type="button"
                  onClick={() => setForm({
                    ...form,
                    dismissed_competitor_suggestions: form.dismissed_competitor_suggestions.filter((d) => d !== dom),
                  })}
                  data-testid={`client-form-unhide-${dom}`}
                  title="Unhide — allow this suggestion to appear again"
                  className="h-4 w-4 rounded-full bg-[#edf2f7] hover:bg-[#cbd5e0] text-[#4a5568] inline-flex items-center justify-center transition-colors"
                >
                  <RotateCcw className="h-2.5 w-2.5" strokeWidth={2.8} />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-[#a0aec0]">
            Save the client to persist your changes. For domains you never want to see on any client, use the global
            blocklist in <strong>App settings</strong>.
          </div>
        </Section>
      )}

      <Section
        icon={FileText}
        title="Report modules"
        description="Choose which sections appear in this client's PDF report (download and monthly email attachment) and drag them into the order you want. Modules with no data are still skipped automatically."
        testid="client-form-section-report-modules"
      >
        <ReportModulesEditor
          value={form.report_modules}
          onChange={(v) => setForm((f) => ({ ...f, report_modules: v }))}
        />
      </Section>

      <Section
        icon={Snowflake}
        title="Freeze client"
        description="For pitch or trial clients. Freezing stops every paid data pull — weekly and monthly checks, digest emails, manual run buttons and live tab lookups. Existing rankings and stats stay visible."
        testid="client-form-section-freeze"
      >
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="client-form-freeze-toggle-label">
          <input
            type="checkbox"
            checked={!!form.frozen}
            onChange={(e) => setForm({ ...form, frozen: e.target.checked })}
            data-testid="client-form-freeze-toggle"
            className="mt-1 h-4 w-4 rounded border-[#cbd5e0] text-[#38b2ac] focus:ring-[#4fd1c5]"
          />
          <div className="flex-1">
            <div className="text-[13px] font-bold text-[#1a202c]">Freeze all rankings and stats</div>
            <div className="text-[11px] text-[#718096] mt-0.5">
              No SEO API usage while frozen. Untick to resume — the next scheduled run picks the client up again.
            </div>
          </div>
        </label>
      </Section>
      </div>
      <DialogFooter className="pt-4 mt-2 gap-2 border-t border-[#e2e8f0] shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="btn-ghost"
          data-testid="client-form-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || logoUploading || keywordStats.over}
          data-testid="client-form-submit"
          className="btn-primary"
        >
          {saving ? "Saving…" : (initial?.id ? "Save changes" : "Create client")}
        </button>
      </DialogFooter>
    </form>
  );
}
